/*
 * certificate-maker/bg-image.js — a photograph made fit to put words on.
 *
 * WHY THE PIXELS ARE CHANGED AND NOT A FILTER APPLIED
 * ──────────────────────────────────────────────────
 * The obvious way to desaturate or blur a background is a filter: `filter:
 * grayscale(60%)` in the SVG, `ctx.filter` on the canvas. Both work, and both
 * are invisible to the third renderer — pdf-lib has no filter operator at all.
 * The sheet would then look one way on screen and another on paper, which is the
 * single failure this tool's whole architecture exists to prevent.
 *
 * So the adjustment is applied ONCE, here, to the image data itself, and all
 * three renderers receive a picture that already is what it should be. Same rule
 * that keyed the white out of a signature scan (2026-08-23): fix the data, not
 * each renderer.
 *
 * WHAT THE CONTROLS ACTUALLY DO FOR THE READER
 * ────────────────────────────────────────────
 * A photograph behind a name fails for three separate reasons, and they need
 * three separate remedies — which is why this is not one «strength» slider:
 *
 *   gray   colour competing with the type    → desaturate
 *   blur   detail competing with the type    → soften
 *   fade   contrast competing with the type  → let the paper through (opacity,
 *          which every renderer DOES support, so it stays a primitive property
 *          and is not baked in — see layout.js)
 *
 * ⚠️ THE ORIGINAL IS KEPT. Every bake starts from the untouched upload, never
 * from the previous bake: chaining blur onto blur is irreversible, and a slider
 * that cannot go back is a slider people stop touching.
 *
 * ⚠️ THE UPLOAD IS DOWNSCALED. 2400 px on the long side is ~205 dpi across an
 * A4, which is past what any office printer resolves, and it is the difference
 * between a 700 KB document and a 12 MB one. Bigger is not sharper on paper; it
 * is only slower here and heavier in the file.
 */
(function (root, factory) {
    'use strict';
    var api = factory();
    if (typeof module !== 'undefined' && module.exports) module.exports = api;
    if (root) root.CertBgImage = api;
})(typeof self !== 'undefined' ? self : (typeof window !== 'undefined' ? window : null), function () {
    'use strict';

    var MAX_SIDE = 2400;
    var LUMA_COLS = 24, LUMA_ROWS = 18;

    function canvas(w, h) {
        var c = document.createElement('canvas');
        c.width = Math.max(1, Math.round(w));
        c.height = Math.max(1, Math.round(h));
        return c;
    }

    /** `ctx.filter` is how the blur is done; without it we still desaturate by
     *  hand rather than pretend the control worked.
     *  ⚠️ Not by reading the value back: on Safari <= 17 (every iPhone browser
     *  on iOS 17 and older) the assignment creates a plain JS property that
     *  reads back the same string, so the old check said «blurred» and the
     *  certificate came out with a sharp background. CanvasCssFilter.works is
     *  true there too — through the page-wide shim — and means the blur IS
     *  drawn (2026-09-27). */
    function hasFilter() {
        try { return !!(window.CanvasCssFilter && window.CanvasCssFilter.works); }
        catch (e) { return false; }
    }

    function load(src) {
        return new Promise(function (res, rej) {
            var img = new Image();
            img.onload = function () { res(img); };
            img.onerror = function () { rej(new Error('image')); };
            img.src = src;
        });
    }

    /** Average luminance on a coarse grid — what the preflight judges the type
     *  against. Measured on the BAKED picture, because that is the one printed:
     *  reading it off the original would report the contrast of a photograph
     *  nobody is going to see. */
    function lumaOf(cv) {
        try {
            var g = canvas(LUMA_COLS, LUMA_ROWS);
            var gc = g.getContext('2d', { willReadFrequently: true });
            gc.drawImage(cv, 0, 0, LUMA_COLS, LUMA_ROWS);
            var d = gc.getImageData(0, 0, LUMA_COLS, LUMA_ROWS).data, v = [];
            for (var i = 0; i < LUMA_COLS * LUMA_ROWS; i++) {
                v.push(Math.round(0.2126 * d[i * 4] + 0.7152 * d[i * 4 + 1] + 0.0722 * d[i * 4 + 2]));
            }
            return { cols: LUMA_COLS, rows: LUMA_ROWS, v: v };
        } catch (e) { return null; }
    }

    /**
     * Normalise an upload: cap the size, drop the alpha onto white and re-encode.
     * Returns { src, ar } — the source every later bake starts from.
     */
    function prepare(dataUrl) {
        return load(dataUrl).then(function (img) {
            var w = img.naturalWidth, h = img.naturalHeight;
            var k = Math.min(1, MAX_SIDE / Math.max(w, h));
            var cv = canvas(w * k, h * k);
            var c = cv.getContext('2d');
            /* A transparent PNG over paper would otherwise carry black through
               the JPEG encoder. */
            c.fillStyle = '#ffffff';
            c.fillRect(0, 0, cv.width, cv.height);
            c.drawImage(img, 0, 0, cv.width, cv.height);
            return { src: cv.toDataURL('image/jpeg', 0.86), ar: w / h };
        });
    }

    /**
     * bake(src, {gray, blur, tint}) → { src, ar, luma }
     *
     *   gray  0…1  how far toward grey
     *   blur  0…1  how soft, as a fraction of the short side (capped)
     *   tint  '#rrggbb' or null — a duotone toward the document's own accent,
     *         which is what makes an arbitrary photograph look chosen rather
     *         than pasted. Applied on top of the desaturation, so it is only
     *         visible once colour is out of the way.
     */
    function bake(src, opts) {
        opts = opts || {};
        var gray = Math.max(0, Math.min(1, opts.gray || 0));
        var blur = Math.max(0, Math.min(1, opts.blur || 0));
        var tint = opts.tint || null;

        return load(src).then(function (img) {
            var w = img.naturalWidth, h = img.naturalHeight;
            /* Blur in pixels, from the SHORT side, so the same slider looks the
               same on a panorama and on a square. */
            var px = blur > 0 ? Math.max(1, Math.round(Math.min(w, h) * blur * 0.06)) : 0;
            var filt = hasFilter();
            var cv = canvas(w, h);
            var c = cv.getContext('2d', { willReadFrequently: true });

            if (px > 0 && filt) {
                /* ⚠️ A blur samples OUTSIDE the picture and the edge fades to
                   transparent — on a full-bleed background that is a pale halo
                   round all four sides of every printed sheet. Draw the source
                   over-sized, blur that, and crop back to the original box. */
                var pad = px * 3;
                var big = canvas(w + pad * 2, h + pad * 2);
                var bc = big.getContext('2d');
                bc.drawImage(img, 0, 0, w, h, 0, 0, big.width, big.height);   // stretch outward
                bc.drawImage(img, pad, pad, w, h);                            // true pixels inside
                var blurred = canvas(big.width, big.height);
                var kc = blurred.getContext('2d');
                kc.filter = 'blur(' + px + 'px)';
                kc.drawImage(big, 0, 0);
                c.drawImage(blurred, pad, pad, w, h, 0, 0, w, h);
            } else {
                c.drawImage(img, 0, 0, w, h);
            }

            if (gray > 0 || tint) {
                var d = c.getImageData(0, 0, w, h);
                var p = d.data;
                var tr = 0, tg = 0, tb = 0;
                if (tint) {
                    var t = String(tint).replace('#', '');
                    if (t.length === 3) t = t[0] + t[0] + t[1] + t[1] + t[2] + t[2];
                    tr = parseInt(t.substr(0, 2), 16) || 0;
                    tg = parseInt(t.substr(2, 2), 16) || 0;
                    tb = parseInt(t.substr(4, 2), 16) || 0;
                }
                for (var i = 0; i < p.length; i += 4) {
                    var y = 0.2126 * p[i] + 0.7152 * p[i + 1] + 0.0722 * p[i + 2];
                    var r = p[i] + (y - p[i]) * gray;
                    var g2 = p[i + 1] + (y - p[i + 1]) * gray;
                    var b2 = p[i + 2] + (y - p[i + 2]) * gray;
                    if (tint) {
                        /* Duotone: the grey ramp is remapped between paper-white
                           and the accent, so midtones take the hue and the ends
                           stay readable. */
                        var f = y / 255;
                        r = r + ((255 - (255 - tr) * (1 - f)) - r) * gray;
                        g2 = g2 + ((255 - (255 - tg) * (1 - f)) - g2) * gray;
                        b2 = b2 + ((255 - (255 - tb) * (1 - f)) - b2) * gray;
                    }
                    p[i] = r; p[i + 1] = g2; p[i + 2] = b2;
                }
                c.putImageData(d, 0, 0);
            }

            return { src: cv.toDataURL('image/jpeg', 0.86), ar: w / h, luma: lumaOf(cv), blurPx: px };
        });
    }

    /**
     * Settings that make THIS picture carry THIS text, derived from the
     * luminance grid rather than from a preference.
     *
     * The rule the numbers come from: the type needs a backdrop far enough from
     * its own lightness, and evenly enough lit that no line lands on a patch
     * that fails. So a busy dark photograph is desaturated, softened and faded
     * until the worst cell under the column is inside the range that reads —
     * which is what the preflight is going to measure anyway.
     */
    function suggest(luma, inkIsDark, paperLum) {
        var paper = paperLum == null ? 250 : paperLum;
        if (!luma || !luma.v || !luma.v.length) {
            return { gray: 0.6, blur: 0.35, fade: 0.45, scrim: 0 };
        }
        var v = luma.v, n = v.length;
        var mean = 0, min = 255, max = 0;
        for (var i = 0; i < n; i++) { mean += v[i]; if (v[i] < min) min = v[i]; if (v[i] > max) max = v[i]; }
        mean /= n;
        var spread = max - min;

        /* Busy → blur. Uneven light is what puts one word on a bright patch and
           the next on a dark one, and no amount of fading fixes that. */
        var blur = Math.max(0.15, Math.min(0.9, spread / 255 * 1.1));
        /* Colour fights the type even when the lightness is fine, and a
           luminance grid cannot see saturation — so this is deliberately
           generous rather than clever. */
        var gray = Math.max(0.35, Math.min(1, 0.35 + spread / 255 * 0.65));

        /* ⚠️ FADING IS COMPOSITING, AND THE ARITHMETIC HAS TO SAY SO. The first
         * version scored «distance from the target» and duly told a pale
         * photograph under dark type to fade by 65 % — a picture that needed
         * nothing at all. Fading means drawing the picture at α over the paper:
         *
         *     result = mean·α + paper·(1 − α)      ⇒  α = (target − paper)
         *                                                 ────────────────
         *                                                  (mean − paper)
         *
         * which answers three different situations honestly instead of one. */
        var target = inkIsDark ? 215 : 60;
        var already = inkIsDark ? (mean >= target) : (mean <= target);
        var fade = 0, scrim = 0;
        if (!already) {
            var alpha = (mean - paper) === 0 ? Infinity : (target - paper) / (mean - paper);
            if (isFinite(alpha) && alpha > 0.08 && alpha <= 1) {
                fade = 1 - alpha;
            } else {
                /* The paper cannot get us there — white type on a white
                 * photograph does not improve by adding more white. That is
                 * what the panel behind the text is for, and saying so is more
                 * use than a slider moved for the look of it. */
                scrim = 0.82;
            }
        }
        /* A picture can also be unusable by being UNEVEN rather than by being
         * the wrong lightness: half the line on snow, half on a shadow. Blur
         * helps, the panel finishes the job. */
        if (spread > 120) scrim = Math.max(scrim, 0.7);

        return {
            gray: Math.round(gray * 100) / 100,
            blur: Math.round(blur * 100) / 100,
            fade: Math.round(Math.max(0, Math.min(0.9, fade)) * 100) / 100,
            scrim: Math.round(scrim * 100) / 100
        };
    }

    return { prepare: prepare, bake: bake, suggest: suggest, lumaOf: lumaOf, MAX_SIDE: MAX_SIDE };
});
